import React from "react";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/lib/i18n";
import { fmtCurrency, fmtPercent, fmtScore } from "@/components/portfolio/_format";
import InfoTooltip from "@/components/portfolio/InfoTooltip";
import ResponsiveKpiValue from "@/components/portfolio/ResponsiveKpiValue";

/* ---------- Partial-coverage badge for FV / Upside cards ----------
 * Phase 8.LP.LR follow-up — when the backend reports
 * ``fair_value_coverage_is_complete === false`` the user-facing KPI
 * must identify that the shown Estimated Fair Value and
 * Upside/Downside cover a SUBSET of holdings, not the whole
 * portfolio.  Localized DE/EN copy with aria-labelled accessible
 * tooltip. */
function PartialCoverageBadge({ s, t, testId }) {
  const covered = s.fair_value_covered_holdings_count ?? 0;
  const total = s.holdings_count ?? 0;
  // Review follow-up (2026-02): ``fair_value_coverage_ratio`` is the
  // share of portfolio MARKET VALUE covered by Fair Value data, not
  // count/total.  Concise visible label — the full explanation lives
  // in the shared disclosure above the summary grid (``PortfolioFVCoverageNotice``)
  // AND in the accessible tooltip here (keyboard + touch via InfoTooltip).
  const ratio = s.fair_value_coverage_ratio ?? null;
  const ratioStr = ratio != null ? `${Math.round(ratio * 100)}%` : "—";
  const badge = t("portfolio.kpi.coverage_partial_badge") || "Partial";
  // Concise visible label — single clause only.
  const shortLabel = (
    t("portfolio.kpi.coverage_partial_short_label") || "{count}/{total} holdings"
  ).replace("{count}", covered).replace("{total}", total);
  // Full disambiguating explanation — rendered inside InfoTooltip so
  // it has native keyboard + touch support without inflating the KPI card.
  const tip = (t("portfolio.kpi.coverage_partial_tooltip") ||
    "Fair Value is available for only {count} of {total} holdings. The percentage shown is the share of portfolio MARKET VALUE those holdings represent, not the share of holdings by count.")
    .replace("{count}", covered).replace("{total}", total);
  return (
    <div
      data-testid={testId}
      data-coverage-ratio={ratio != null ? ratio.toFixed(4) : "null"}
      data-coverage-ratio-basis="market_value"
      data-coverage-complete="false"
      className="mt-2 flex items-center gap-1.5 flex-wrap"
    >
      <span
        className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-50 px-1.5 py-[1px] text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-500/15 dark:text-amber-200"
        data-testid={`${testId}-chip`}
        aria-label={tip}
      >
        {badge} · {ratioStr}
      </span>
      <span className="text-[11px] text-muted-foreground" data-testid={`${testId}-label`}>
        {shortLabel}
      </span>
      <InfoTooltip
        text={tip}
        label={t("portfolio.kpi.coverage_partial_badge") || "Partial coverage details"}
        testId={`${testId}-info`}
        side="top"
      />
    </div>
  );
}

/**
 * Shared "share of portfolio market value" disclosure.
 *
 * 2026-02 Staging regression follow-up — the FV and Upside KPI cards
 * each need to disambiguate value-weighted coverage from count-based
 * coverage, but putting the full explanation inside EVERY card bloats
 * the grid (especially on mobile). We render ONE compact notice above
 * the cards instead, keeping the per-card badge tight.
 */
function PartialCoverageNotice({ s, t }) {
  if (s?.fair_value_coverage_is_complete !== false) return null;
  if (!s?.fair_value_covered_holdings_count) return null;
  const covered = s.fair_value_covered_holdings_count ?? 0;
  const total = s.holdings_count ?? 0;
  const ratio = s.fair_value_coverage_ratio ?? null;
  const ratioStr = ratio != null ? `${Math.round(ratio * 100)}%` : "—";
  const notice = (
    t("portfolio.kpi.coverage_partial_notice") ||
    "Fair Value shown covers {count} of {total} holdings ({ratio} of portfolio market value) — not the whole portfolio."
  )
    .replace("{count}", covered)
    .replace("{total}", total)
    .replace("{ratio}", ratioStr);
  return (
    <div
      role="note"
      data-testid="portfolio-partial-coverage-notice"
      data-coverage-ratio={ratio != null ? ratio.toFixed(4) : "null"}
      data-coverage-ratio-basis="market_value"
      className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-900 dark:text-amber-100 flex items-start gap-2"
    >
      <span
        className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-50 px-1.5 py-[1px] text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-500/15 dark:text-amber-200 shrink-0"
      >
        {t("portfolio.kpi.coverage_partial_badge") || "Partial"}
      </span>
      <span className="leading-snug">{notice}</span>
    </div>
  );
}

function Card({ title, accent = "cyan", testId, children, dataValueTestId, tooltipText, tooltipTestId, gated = false }) {
  const dotColors = {
    cyan: "bg-[#3DEBFF] shadow-[0_0_18px_rgba(61,235,255,0.45)]",
    green: "bg-[#2BFF8A] shadow-[0_0_18px_rgba(43,255,138,0.4)]",
    indigo: "bg-[#A78BFA] shadow-[0_0_18px_rgba(167,139,250,0.4)]",
    loss: "bg-[#FF3DA6] shadow-[0_0_18px_rgba(255,61,166,0.4)]",
  };
  return (
    <div
      data-testid={testId}
      data-gated={gated ? "true" : undefined}
      className="relative overflow-hidden rounded-xl bg-card/60 backdrop-blur-xl border border-white/10 p-4 sm:p-5 shadow-[var(--app-shadow-elev-1)]"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-1.5 min-w-0 flex-1">
          {/* Sub-pass 2A (2026-02-17) — allow the KPI heading to
              wrap to a second line on any width instead of being
              truncated with an ellipsis.  Full labels like
              "Weighted Opportunity Score" and "Estimated Fair
              Value" must remain fully visible on normal desktop.
              `leading-[1.35]` keeps the two-line height comfortable
              without enlarging the card, and `break-words` guards
              against very long localized copy. */}
          <div
            className="min-w-0 flex-1 text-[10px] uppercase tracking-[0.1em] text-muted-foreground leading-[1.35] break-words hyphens-auto"
            data-testid={testId ? `${testId}-title` : undefined}
          >{title}</div>
          {tooltipText ? (
            <InfoTooltip text={tooltipText} testId={tooltipTestId} label={title} />
          ) : null}
        </div>
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full mt-1 ${dotColors[accent] || dotColors.cyan}`} />
      </div>
      {/* Phase 8.WLNAV.v1.5 — in preview mode gated cards blur only their
          content payload (value + supporting text). Title / dot / border
          remain fully sharp per spec. */}
      {gated ? (
        <div
          className="mt-2 [filter:blur(6px)] select-none pointer-events-none"
          aria-hidden="true"
          data-testid={dataValueTestId}
          data-preview-gated="true"
        >
          {children}
        </div>
      ) : (
        <div className="mt-2" data-testid={dataValueTestId}>{children}</div>
      )}
    </div>
  );
}

function Delta({ value, format = "percent", invert = false }) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const positive = invert ? value < 0 : value >= 0;
  const color = positive ? "text-[#2BFF8A]" : "text-[#FF3DA6]";
  const text = format === "percent" ? fmtPercent(value) : fmtCurrency(value);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/20 px-2 py-0.5 text-xs tabular ${color}`}>
      {value >= 0 ? "+" : ""}{text}
    </span>
  );
}

export default function PortfolioSummaryCards({ summary, dominantCurrency = null, previewMode = false }) {
  const { t, locale } = useTranslation();
  const s = summary || {};
  const score = s.weighted_ai_opportunity_score;
  const risk = (s.weighted_risk_level || "").toLowerCase();
  const cur = dominantCurrency || "USD";
  const riskTone = {
    low: "bg-[#2BFF8A]/15 text-[#2BFF8A] border-[#2BFF8A]/40",
    medium: "bg-[#F0A93B]/15 text-[#F0A93B] border-[#F0A93B]/40",
    high: "bg-[#FF3DA6]/15 text-[#FF3DA6] border-[#FF3DA6]/40",
  };
  // Phase 8.1.c — when there is NO price coverage at all, KPI cards
  // show "Analysis pending" instead of fake zeros / -100% losses.
  const mvAvailable = s.total_market_value !== null && s.total_market_value !== undefined;
  const pnlAvailable = s.total_unrealized_pnl !== null && s.total_unrealized_pnl !== undefined;
  const pending = t("portfolio.common.pending");

  return (
    <div data-testid="portfolio-summary-cards-wrap" lang={locale} className="space-y-3">
      <PartialCoverageNotice s={s} t={t} />
      <div
        data-testid="portfolio-summary-cards"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4"
      >
      <Card title={t("portfolio.kpi.value")} accent="cyan" testId="portfolio-kpi-portfolio-value-card" dataValueTestId="portfolio-kpi-portfolio-value-wrap" tooltipText={t("portfolio.tooltip.portfolio_value")} tooltipTestId="portfolio-kpi-portfolio-value-tooltip">
        {mvAvailable ? (
          <ResponsiveKpiValue
            value={s.total_market_value}
            currency={cur}
            ifNull={pending}
            testId="portfolio-kpi-portfolio-value"
            ariaLabelBase={t("portfolio.kpi.value")}
          />
        ) : (
          <div className="font-display text-2xl sm:text-3xl tabular leading-none" data-testid="portfolio-kpi-portfolio-value">{pending}</div>
        )}
        <div className="mt-2 text-xs text-muted-foreground">
          <span className="tabular">{t("portfolio.kpi.cost_basis")}: {fmtCurrency(s.total_cost_basis, { currency: cur, ifNull: "—" })}</span>
          {mvAvailable ? (<span className="ml-2"><Delta value={s.total_unrealized_pnl_pct} format="percent" /></span>) : null}
        </div>
      </Card>

      <Card title={t("portfolio.kpi.estimated_fair_value")} accent="green" testId="portfolio-kpi-estimated-fair-value-card" dataValueTestId="portfolio-kpi-estimated-fair-value-wrap" tooltipText={t("portfolio.tooltip.estimated_fair_value")} tooltipTestId="portfolio-kpi-estimated-fair-value-tooltip">
        {s.estimated_portfolio_fair_value !== null && s.estimated_portfolio_fair_value !== undefined ? (
          <ResponsiveKpiValue
            value={s.estimated_portfolio_fair_value}
            currency={cur}
            ifNull={pending}
            testId="portfolio-kpi-estimated-fair-value"
            ariaLabelBase={t("portfolio.kpi.estimated_fair_value")}
          />
        ) : (
          <div className="font-display text-2xl sm:text-3xl tabular leading-none" data-testid="portfolio-kpi-estimated-fair-value">{pending}</div>
        )}
        <div className="mt-2 text-xs text-muted-foreground">
          <Delta value={s.estimated_portfolio_upside_downside_pct} format="percent" />
          <span className="ml-2">{s.fair_value_coverage_is_complete === false ? t("portfolio.kpi.coverage_vs_covered_value") : t("portfolio.kpi.vs_value")}</span>
        </div>
        {/* Phase 8.LP.LR — visible Partial-coverage badge on the FV card. */}
        {s.fair_value_coverage_is_complete === false && s.fair_value_covered_holdings_count ? (
          <PartialCoverageBadge s={s} t={t} testId="portfolio-kpi-estimated-fair-value-coverage" />
        ) : null}
      </Card>

      <Card title={t("portfolio.kpi.upside_downside")} accent="cyan" testId="portfolio-kpi-upside-downside-card" dataValueTestId="portfolio-kpi-upside-downside" tooltipText={t("portfolio.tooltip.portfolio_upside")} tooltipTestId="portfolio-kpi-upside-downside-tooltip" gated={previewMode}>
        <div className="font-display text-2xl sm:text-3xl tabular leading-none">
          {s.estimated_portfolio_upside_downside_pct === null || s.estimated_portfolio_upside_downside_pct === undefined
            ? pending
            : `${s.estimated_portfolio_upside_downside_pct >= 0 ? "+" : ""}${fmtPercent(s.estimated_portfolio_upside_downside_pct)}`}
        </div>
        <div className="mt-2 text-xs text-muted-foreground">{t("portfolio.kpi.upside_downside_hint")}</div>
        {s.fair_value_coverage_is_complete === false && s.fair_value_covered_holdings_count ? (
          <PartialCoverageBadge s={s} t={t} testId="portfolio-kpi-upside-downside-coverage" />
        ) : null}
      </Card>

      <Card title={t("portfolio.kpi.unrealized_pl")} accent={!pnlAvailable ? "indigo" : ((s.total_unrealized_pnl || 0) >= 0 ? "green" : "loss")} testId="portfolio-kpi-unrealized-pl-card" dataValueTestId="portfolio-kpi-unrealized-pl-wrap" tooltipText={t("portfolio.tooltip.unrealized_pl")} tooltipTestId="portfolio-kpi-unrealized-pl-tooltip" gated={previewMode}>
        {pnlAvailable ? (
          <ResponsiveKpiValue
            value={s.total_unrealized_pnl}
            currency={cur}
            signed
            ifNull={pending}
            testId="portfolio-kpi-unrealized-pl"
            ariaLabelBase={t("portfolio.kpi.unrealized_pl")}
            colorClass={(s.total_unrealized_pnl || 0) >= 0 ? "text-[#2BFF8A]" : "text-[#FF3DA6]"}
          />
        ) : (
          <div className="font-display text-2xl sm:text-3xl tabular leading-none text-muted-foreground" data-testid="portfolio-kpi-unrealized-pl">{pending}</div>
        )}
        <div className="mt-2 text-xs text-muted-foreground">
          {pnlAvailable ? <Delta value={s.total_unrealized_pnl_pct} format="percent" /> : <span>{t("portfolio.kpi.unrealized_pl_hint_pending")}</span>}
        </div>
      </Card>

      <Card title={t("portfolio.kpi.weighted_score")} accent="green" testId="portfolio-kpi-weighted-opportunity-score-card" dataValueTestId="portfolio-kpi-weighted-opportunity-score" tooltipText={t("portfolio.tooltip.weighted_opportunity_score")} tooltipTestId="portfolio-kpi-weighted-opportunity-score-tooltip" gated={previewMode}>
        <div className="font-display text-2xl sm:text-3xl tabular leading-none">{score === null || score === undefined ? pending : fmtScore(score)}</div>
        <div className="mt-2 text-xs text-muted-foreground">{t("portfolio.kpi.weighted_score_hint")}</div>
      </Card>

      <Card title={t("portfolio.kpi.risk_level")} accent="indigo" testId="portfolio-kpi-risk-level-card" dataValueTestId="portfolio-kpi-risk-level" tooltipText={t("portfolio.tooltip.risk_level")} tooltipTestId="portfolio-kpi-risk-level-tooltip">
        {risk ? (
          <Badge
            data-testid="portfolio-kpi-risk-level-pill"
            className={`px-3 py-1 text-sm capitalize border ${riskTone[risk] || riskTone.medium}`}
            variant="outline"
          >
            {t(`portfolio.risk.${risk}`)}
          </Badge>
        ) : (
          <div className="text-base text-muted-foreground">{pending}</div>
        )}
        <div className="mt-2 text-xs text-muted-foreground">{t("portfolio.kpi.risk_hint")}</div>
      </Card>
      </div>
    </div>
  );
}
